// Task mutations, independent of which workspace is active (#238).
//
// Each one owns the whole sequence — cycle guard, database write, audit entry,
// notifications — and takes *how* optimistic state is applied as a callback,
// because that differs between the zustand store and a caller holding one row.
import { applyWrite } from '@/shared/lib/store-write';
import { normalizeTaskPatch, taskDbUpdates, taskTitleProblem } from '@/modules/tasks/task-rules';
import type { Task, TaskQueuedAction } from '@/modules/tasks/types';
import { storeNotice } from '@/shared/lib/store-notice';

import type { MutationContext } from '@/modules/activity/mutation-context';

export { normalizeTaskPatch, taskDbUpdates };

function taskApiBody(ctx: MutationContext, db: Record<string, unknown>): Record<string, unknown> {
  const { owner_id, ...rest } = db;
  return {
    ...rest,
    ...('owner_id' in db ? { assignee_id: owner_id ?? null } : {}),
    unlocked_cycle: ctx.unlockedCycleKey?.() ?? null,
  };
}

export function addTask(
  ctx: MutationContext,
  item: Task,
  hooks: { apply(task: Task): void; rollback(): void; unassign?(): void },
): void {
  if (ctx.isBlocked(item.year, item.quarter)) return;
  const titleProblem = taskTitleProblem(item.title);
  if (titleProblem) {
    storeNotice.error(titleProblem.message);
    return;
  }
  const ownerId = ctx.resolveOwnerId(item.ownerId);
  const status = item.status ?? (item.completed ? 'done' : 'open');
  const completedAt = status === 'done' ? (item.completedAt ?? new Date().toISOString()) : undefined;
  const task: Task = {
    ...item,
    teamId: item.teamId ?? ctx.defaultTeamId(),
    status,
    ownerId: ownerId ?? item.ownerId,
    completedAt,
    completed: status === 'done',
  };
  hooks.apply(task);
  applyWrite(ctx.api('/tasks', {
    id: task.id,
    workspace_id: ctx.workspaceId,
    ...(task.teamId ? { team_id: task.teamId } : {}),
    title: task.title,
    assignee_id: ownerId ?? null,
    due_date: task.dueDate ?? null,
    status,
    completed_at: completedAt ?? null,
    issue_id: task.issueId ?? null,
    milestone_id: task.milestoneId ?? null,
    rock_id: task.rockId ?? null,
    delay_count: task.delayCount ?? 0,
    year: task.year ?? null,
    quarter: task.quarter ?? null,
    unlocked_cycle: ctx.unlockedCycleKey?.() ?? null,
  }), {
    noun: 'this to-do',
    context: 'addTask',
    rollback: hooks.rollback,
    onSuccess: (rows) => {
      const stored = rows[0] as { assignee_id?: string | null } | undefined;
      if (task.ownerId && stored && !stored.assignee_id) hooks.unassign?.();
    },
  });
}

export function updateTask(
  ctx: MutationContext,
  id: string,
  updates: Partial<Task>,
  before: Task | undefined,
  hooks: {
    apply(patch: Partial<Task>): void;
    rollback(): void;
    /** The rock this to-do advances, for the completion notification. */
    rockOwnerId?: string;
  },
): void {
  if (before && ctx.isBlocked(before.year, before.quarter)) return;
  const patch = normalizeTaskPatch(updates);
  hooks.apply(patch);

  const db = taskDbUpdates(patch);
  if (!Object.keys(db).length) return;

  applyWrite(ctx.api(`/tasks/${id}`, taskApiBody(ctx, db), 'PATCH'), {
    noun: 'this to-do',
    context: 'updateTask',
    rollback: hooks.rollback,
  });
}

export function deleteTask(
  ctx: MutationContext,
  id: string,
  before: Task | undefined,
  hooks: { apply(): void },
): void {
  if (before && ctx.isBlocked(before.year, before.quarter)) return;
  hooks.apply();
  applyWrite(ctx.api(`/tasks/${id}`, taskApiBody(ctx, {}), 'DELETE'), {
    noun: 'this to-do',
    context: 'deleteTask',
    operation: 'delete',
    refetch: ctx.refetch,
  });
}

export function queueTaskAction(
  ctx: MutationContext,
  id: string,
  action: TaskQueuedAction | null,
  hooks: { apply(): void },
): void {
  hooks.apply();
  applyWrite(
    ctx.api(`/tasks/${id}`, taskApiBody(ctx, { queued_action: action ?? null }), 'PATCH'),
    { noun: 'this to-do', context: 'queueTaskAction', refetch: ctx.refetch },
  );
}
